import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Col, Row, Typography } from 'antd'
import Skeleton from 'react-loading-skeleton'

import B2BecCartSummary from '@/components/B2BecCartSummary'
import CustomButton from '@/components/CustomButton'
import FormatStatus from '@/components/FormatStatus'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import { selectHideVoucher } from '@/store/selectors/configSelector'
import { reorder } from '@/store/slices/cartSlice'
import usePermission from '@/hooks/usePermission'
import { MODULE_ORDER_RIGHTS, MODULE_SHOP_NAVIGATION_RIGHTS } from '@/libs/constants/modulerights'
import { formatDate, getFormatType } from '@/libs/utils/dateUtils'

import styles from './OrderDetailsPage.module.scss'

const { Title, Text } = Typography

interface B2BecOrderDetailTitleProps {
  isLoading: boolean
  showSummary?: boolean
  orderData: {
    salesOrderNumber: string
    orderDate: string
    referenceNumber: string
    status: string
    additionalCost?: number
    cartSubTotal?: number
    cartTotal?: number
    cartTotalVAT?: number
    shippingCosts?: number
    vat?: number
    checkoutSettings?: unknown
    currencyUnit?: string
    voucherValue?: number
    invoicesNumbers?: string[]
    items?: unknown[]
    deliveryDates?: unknown[]
    shipments?: string[]
    hideVATFields?: boolean
    hideStatusColumn?: boolean
  }
  hideStatusColumn?: boolean
  hideVATFields?: boolean
}

const B2BecOrderDetailTitle = ({
  isLoading,
  showSummary,
  orderData: orderDetails,
  hideStatusColumn,
  hideVATFields,
}: B2BecOrderDetailTitleProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const hideVoucher = useAppSelector(selectHideVoucher)
  const {
    verifiedPermissions: [hasReorderPermission, canViewCart],
  } = usePermission([
    MODULE_ORDER_RIGHTS.REORDER_HISTORIC_ORDER,
    MODULE_SHOP_NAVIGATION_RIGHTS.VIEW_CART,
  ])

  const salesOrderNumber = orderDetails?.salesOrderNumber
  const orderDate = orderDetails?.orderDate
  const referenceNumber = orderDetails?.referenceNumber
  const status = orderDetails?.status

  const reorderHandler = useCallback(() => {
    if (!salesOrderNumber || !hasReorderPermission) {
      return
    }

    dispatch(reorder({ salesOrderNumber, canViewCart, navigate }))
  }, [dispatch, navigate, salesOrderNumber, hasReorderPermission, canViewCart])

  return (
    <div className={styles.OrderDetailsPageTitle}>
      <h1>
        {t('orderDetail.order')} {salesOrderNumber}
      </h1>
      <Row gutter={[16, 16]}>
        <Col lg={{ span: showSummary ? 16 : 24 }} span={24}>
          <Row gutter={[16, 16]}>
            <Col span={showSummary ? 12 : 8}>
              <Title level={3}>{t('orderDetail.orderDate')}</Title>
              <Text>
                {isLoading ? <Skeleton /> : (
                  formatDate(orderDate, getFormatType().default)
                  || t('unavailableDate')
                )}
              </Text>
            </Col>
            <Col span={showSummary ? 12 : 8}>
              <Title level={3}>{t('checkout.step2.referenceNumber')}</Title>
              <Text>{isLoading ? <Skeleton /> : referenceNumber}</Text>
            </Col>
            {hideStatusColumn ? null : (
              <Col span={showSummary ? 12 : 8}>
                <Title level={3}>{t('orderDetail.status')}</Title>
                <Text>
                  {isLoading ? <Skeleton /> : <FormatStatus status={status} />}
                </Text>
              </Col>
            )}
          </Row>
        </Col>
        {showSummary && (
          <Col lg={{ span: 8 }} span={24}>
            <div className={styles.summary}>
              <B2BecCartSummary
                additionalCost={orderDetails?.additionalCost}
                cartSubTotal={orderDetails?.cartSubTotal}
                cartTotal={orderDetails?.cartTotal}
                cartTotalVAT={orderDetails?.cartTotalVAT}
                checkoutSettings={orderDetails?.checkoutSettings}
                currencyUnit={orderDetails?.currencyUnit}
                isLoading={isLoading}
                shippingCosts={orderDetails?.shippingCosts}
                vat={orderDetails?.vat}
                voucherValue={orderDetails?.voucherValue}
                hideVAT={hideVATFields}
                hideVoucher={hideVoucher}
              />
              {salesOrderNumber && (
                <CustomButton
                  block
                  disabled={isLoading || !hasReorderPermission}
                  onClick={reorderHandler}
                >
                  {t('reorder')}
                </CustomButton>
              )}
            </div>
          </Col>
        )}
      </Row>
    </div>
  )
}

export default B2BecOrderDetailTitle
